import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Mail, FileText, Send, RefreshCw, Save, Undo2, Inbox, AlertTriangle, Play,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { EmptyState, CardSkeleton } from './common/States';
import { emailAdminAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const statusVariant = (s) => ({
  delivered: 'default',
  sent: 'default',
  queued: 'secondary',
  retry: 'secondary',
  blocked: 'outline',
  failed: 'destructive',
}[s] || 'secondary');

const Section = ({ icon: Icon, title, description, action, loading, isEmpty, emptyText, children }) => (
  <Card>
    <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
      <div className="space-y-1">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </CardHeader>
    <CardContent>
      {loading && <CardSkeleton rows={4} />}
      {!loading && isEmpty && <EmptyState compact title={emptyText} />}
      {!loading && !isEmpty && children}
    </CardContent>
  </Card>
);

/** Stands in for "no tenant" in the picker; state still holds ''. */
const GLOBAL = '__global__';

/**
 * Transactional email, as the superadmin owns it: who each tenant sends as,
 * what the messages say, and what the queue is doing.
 */
const EmailAdminModule = ({ currentTab = 'identities' }) => {
  const [loading, setLoading] = useState(true);
  const [identities, setIdentities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [queue, setQueue] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState({ subject_template: '', body_template: '', body_html: '' });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      emailAdminAPI.listIdentities(),
      emailAdminAPI.listTemplates(tenantId || undefined),
      emailAdminAPI.queue({ limit: 40 }),
    ]);
    const value = (r, fallback) => (r.status === 'fulfilled' ? r.value.data : fallback);
    setIdentities(value(results[0], []));
    setTemplates(value(results[1], []));
    setQueue(value(results[2], []));
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const withoutIdentity = useMemo(
    () => identities.filter((i) => !i.from_email),
    [identities],
  );

  const saveIdentity = async (row, patch) => {
    setBusy(true);
    try {
      await emailAdminAPI.saveIdentity(row.tenant_id, {
        from_email: patch.from_email ?? row.from_email ?? '',
        from_name: patch.from_name ?? row.from_name ?? '',
        reply_to: patch.reply_to ?? row.reply_to ?? '',
        is_active: true,
      });
      toast.success(`Saved for ${row.tenant_name}`);
      load();
    } catch (error) {
      // Policy refusals come back verbatim — the operator needs the reason.
      toast.error(getErrorMessage(error, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  const testSend = async (row) => {
    setBusy(true);
    try {
      const { data } = await emailAdminAPI.testIdentity(row.tenant_id);
      toast.success(`Sent as ${data.from} via ${data.provider}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Test send failed'));
    } finally {
      setBusy(false);
    }
  };

  const openTemplate = (template) => {
    setSelected(template);
    setDraft({
      subject_template: template.subject_template || '',
      body_template: template.body_template || '',
      body_html: template.body_html || '',
    });
    setPreview(null);
  };

  const runPreview = async () => {
    if (!selected) return;
    try {
      const { data } = await emailAdminAPI.previewTemplate(selected.template_key, {
        ...draft,
        tenant_id: tenantId ? Number(tenantId) : undefined,
      });
      setPreview(data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Preview failed'));
    }
  };

  const saveTemplate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await emailAdminAPI.saveTemplate(
        selected.template_key,
        { ...draft, channel: selected.channel, is_active: true },
        tenantId ? Number(tenantId) : undefined,
      );
      toast.success(tenantId ? 'Saved as a tenant override' : 'Global template saved');
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not save template'));
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    if (!selected || !tenantId) return;
    try {
      await emailAdminAPI.revertTemplate(selected.template_key, Number(tenantId));
      toast.success('Reverted to the global default');
      setSelected(null);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Nothing to revert'));
    }
  };

  const drain = async () => {
    setBusy(true);
    try {
      const { data } = await emailAdminAPI.drain(100);
      toast.success(
        `${data.delivered} delivered, ${data.blocked} blocked, ${data.retried} to retry`,
      );
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Drain failed'));
    } finally {
      setBusy(false);
    }
  };

  const tenantPicker = (
    // Radix reserves "" to mean "cleared", so global defaults need a sentinel.
    <Select value={tenantId || GLOBAL} onValueChange={(v) => setTenantId(v === GLOBAL ? '' : v)}>
      <SelectTrigger className="w-56"><SelectValue placeholder="Global defaults" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={GLOBAL}>Global defaults</SelectItem>
        {identities.map((i) => (
          <SelectItem key={i.tenant_id} value={String(i.tenant_id)}>{i.tenant_name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (currentTab === 'identities') {
    return (
      <div className="space-y-6 animate-fade-in">
        {!!withoutIdentity.length && (
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="text-sm">
              <span className="font-medium">
                {withoutIdentity.length} tenant{withoutIdentity.length === 1 ? '' : 's'} cannot send mail.
              </span>{' '}
              <span className="text-muted-foreground">
                Notifications for them are recorded and never delivered — set a
                sending address below.
              </span>
            </div>
          </div>
        )}
        <Section
          icon={Mail}
          title="Sending addresses"
          description="What each tenant's transactional mail goes out as."
          action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
          loading={loading}
          isEmpty={!identities.length}
          emptyText="No tenants."
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>From address</TableHead>
                <TableHead>Display name</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {identities.map((row) => (
                <IdentityRow
                  key={row.tenant_id}
                  row={row}
                  busy={busy}
                  onSave={saveIdentity}
                  onTest={testSend}
                />
              ))}
            </TableBody>
          </Table>
        </Section>
      </div>
    );
  }

  if (currentTab === 'templates') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={FileText}
          title="Templates"
          description={
            tenantId
              ? 'Editing this tenant. Saving creates an override; the global default stays untouched.'
              : 'Editing the global defaults every tenant inherits.'
          }
          action={tenantPicker}
          loading={loading}
          isEmpty={!templates.length}
          emptyText="No templates."
        >
          <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
            <div className="max-h-[32rem] overflow-y-auto space-y-1 pr-1">
              {templates.map((t) => (
                <button
                  key={t.template_key}
                  type="button"
                  onClick={() => openTemplate(t)}
                  className={`w-full rounded-lg border p-2.5 text-left text-sm transition-colors ${
                    selected?.template_key === t.template_key
                      ? 'border-primary bg-muted'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{t.template_key}</span>
                    {t.is_override && <Badge variant="outline" className="shrink-0">Custom</Badge>}
                  </div>
                </button>
              ))}
            </div>

            {!selected ? (
              <EmptyState compact title="Pick a template to edit." />
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={draft.subject_template}
                    onChange={(e) => setDraft({ ...draft, subject_template: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Body (plain text)</Label>
                  <Textarea
                    rows={12}
                    className="font-mono text-xs"
                    value={draft.body_template}
                    onChange={(e) => setDraft({ ...draft, body_template: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Available variables — click to copy
                  </Label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(selected.variables || []).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(`{{${v}}}`)}
                        className="rounded border border-border px-1.5 py-0.5 font-mono text-[11px] hover:bg-muted"
                      >
                        {`{{${v}}}`}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={runPreview}>Preview</Button>
                  <Button size="sm" onClick={saveTemplate} disabled={busy}>
                    <Save className="h-4 w-4 mr-1" /> Save
                  </Button>
                  {tenantId && selected.is_override && (
                    <Button variant="ghost" size="sm" onClick={revert}>
                      <Undo2 className="h-4 w-4 mr-1" /> Revert to global
                    </Button>
                  )}
                </div>

                {preview && (
                  <div className="rounded-lg border border-border p-4 space-y-3">
                    {!!preview.missing_variables?.length && (
                      <div className="flex items-start gap-2 text-sm">
                        <AlertTriangle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
                        <span>
                          Unresolved:{' '}
                          <span className="font-mono">{preview.missing_variables.join(', ')}</span>
                          {' '}— check the spelling before saving.
                        </span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      From: {preview.from || 'no sending address configured'}
                    </div>
                    <div className="font-medium text-sm">{preview.subject}</div>
                    <pre className="whitespace-pre-wrap text-xs text-muted-foreground">
                      {preview.body_text}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </Section>
      </div>
    );
  }

  // queue
  return (
    <div className="space-y-6 animate-fade-in">
      <Section
        icon={Inbox}
        title="Delivery queue"
        description="Everything queued, delivered, blocked or failed."
        action={(
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={drain} disabled={busy}>
              <Play className="h-4 w-4 mr-1" /> Send now
            </Button>
            <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
          </div>
        )}
        loading={loading}
        isEmpty={!queue.length}
        emptyText="Queue is empty."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead>Attempts</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {queue.map((row) => (
              <TableRow key={row.id}>
                <TableCell><Badge variant={statusVariant(row.status)}>{row.status}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{row.template_key || '—'}</TableCell>
                <TableCell className="text-muted-foreground">{row.recipient}</TableCell>
                <TableCell className="text-muted-foreground">{row.attempts}</TableCell>
                <TableCell className="text-xs text-destructive">{row.last_error || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </div>
  );
};

/** One tenant's sending address, editable in place. */
const IdentityRow = ({ row, busy, onSave, onTest }) => {
  const [form, setForm] = useState({
    from_email: row.from_email || '',
    from_name: row.from_name || '',
  });
  const dirty =
    form.from_email !== (row.from_email || '') || form.from_name !== (row.from_name || '');

  return (
    <TableRow>
      <TableCell className="font-medium">{row.tenant_name}</TableCell>
      <TableCell>
        <Input
          className="h-8"
          placeholder="hr@tenant-domain.com"
          value={form.from_email}
          onChange={(e) => setForm({ ...form, from_email: e.target.value })}
        />
      </TableCell>
      <TableCell>
        <Input
          className="h-8"
          placeholder="Tenant HR"
          value={form.from_name}
          onChange={(e) => setForm({ ...form, from_name: e.target.value })}
        />
      </TableCell>
      <TableCell>
        {row.inherited_from
          ? <Badge variant="outline">{row.inherited_from.replace('_', ' ')}</Badge>
          : row.from_email
            ? <Badge>own</Badge>
            : <span className="text-xs text-destructive">not configured</span>}
      </TableCell>
      <TableCell className="text-right space-x-1">
        <Button size="sm" disabled={!dirty || busy} onClick={() => onSave(row, form)}>Save</Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !row.from_email}
          onClick={() => onTest(row)}
        >
          <Send className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default EmailAdminModule;
